import { FC, useState } from "react";

import { Link } from "../../../../utils/routing";
import { pageUrl } from "../../../../utils/urls";

import { PageLink } from "./PageLink";
import { PublishStatus } from "./PublishStatus";
import { AllAssessmentDashboardDataType } from "./hooks";
import { getAssessmentStatusLabel } from "./utils";

import { AssessmentStatus } from "api/api-data-types";
import {
  ASSESSMENT_VIEW_QUERY_PARAM,
  SECONDARY_OVERLAY_QUERY_PARAM,
  SecondaryOverlay,
} from "components/constants";
import { DueDateButton } from "components/materials/page/DueDateButton";
import { AssessedCount } from "components/materials/page/assessment/AssessedCount";
import { MarkAssessmentCompleteMenu } from "components/materials/section/assessment/helpers";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { CircleInfoIcon } from "mds/icons";

interface StatusCellProps {
  row: AllAssessmentDashboardDataType["allAssessmentsTableRows"][number];
  sectionId: string;
}

const StatusCell: FC<StatusCellProps> = ({ row, sectionId }) => {
  const isNotAssessable = !row.status || row.status === AssessmentStatus.NOT_ASSESSABLE;

  if (isNotAssessable) {
    if (!row.page.due_at) return <>-</>;
    return (
      <DueDateButton kind="list-item" page={row.page} clickStopPropagation readOnly shortText />
    );
  }

  const isComplete = row.isAssessmentComplete || row.isMarkedComplete;

  return (
    <SquareButton
      as={Link}
      eventName={`Course - All Assessments / ${isComplete ? "View assessed page" : "Start assessing"}`}
      eventProps={{
        pageTitle: row.page.title,
        pageUrl: pageUrl(sectionId, row.page.id),
        pageId: row.page.id,
      }}
      kind={isComplete ? "complete" : "accent"}
      size="xs"
      to={
        isComplete
          ? pageUrl(sectionId, row.page.id, {
              additionalParams: `${SECONDARY_OVERLAY_QUERY_PARAM}=${SecondaryOverlay.AssessmentSummary}`,
            })
          : pageUrl(sectionId, row.page.id, {
              additionalParams: `${ASSESSMENT_VIEW_QUERY_PARAM}=1`,
            })
      }
    >
      {getAssessmentStatusLabel(
        row.isMarkedComplete,
        row.assessedSubmissionsCount,
        row.isAssessmentComplete,
      )}
      {row.unassessedLateCount > 0 && (
        <Tooltip
          text={t("assessment.late_assessment_count", {
            count: row.unassessedLateCount,
          })}
        >
          <CircleInfoIcon />
        </Tooltip>
      )}
    </SquareButton>
  );
};

type AssessmentStatusTableProps = {
  allAssessmentsCSVData: AllAssessmentDashboardDataType["allAssessmentsCSVData"];
  allAssessmentsTableRows: AllAssessmentDashboardDataType["allAssessmentsTableRows"];
  sectionId: string;
  title: string;
  hideMarkCompleteMenu?: boolean;
  emptyDescription: string;
};

export const AssessmentStatusTable: FC<AssessmentStatusTableProps> = ({
  allAssessmentsCSVData,
  allAssessmentsTableRows,
  sectionId,
  title,
  hideMarkCompleteMenu,
  emptyDescription,
}) => {
  const [, setDummyData] = useState({});
  const refetch = () => setDummyData({}); // Dummy state update to trigger refetch

  if (allAssessmentsTableRows.length === 0) {
    return (
      <ScrollableTableContainer title={title}>
        <div className="ml-4 text-black-tint-40">{emptyDescription}</div>
      </ScrollableTableContainer>
    );
  }

  return (
    <ScrollableTableContainer exportData={allAssessmentsCSVData} title={title}>
      <Table>
        {/* Headers: Topic / Page / Status / Publishing / Assessed / Page Type /Avg. Points / Outcomes / Last Submitted */}
        <THead>
          <TR className="border-black-tint-70">
            <TH className="w-6/12 pl-4">{t("assessment_dashboard.all.page_topic_header_title")}</TH>

            <TH className="w-6/12">{t("assessment_dashboard.all.page_header_title")}</TH>

            <TH className="w-16">{t("assessment_dashboard.all.status_header_title")}</TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.publishing_header_tooltip")}>
                {t("assessment_dashboard.all.publishing_header_title")}
              </Tooltip>
            </TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.assessed_header_tooltip")}>
                {t("assessment_dashboard.all.assessed_header_title")}
              </Tooltip>
            </TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.page_type_header_tooltip")}>
                {t("assessment_dashboard.all.page_type_header_title")}
              </Tooltip>
            </TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.avg_points_header_tooltip")}>
                {t("assessment_dashboard.all.avg_points_header_title")}
              </Tooltip>
            </TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.outcomes_header_tooltip")}>
                {t("assessment_dashboard.all.outcomes_header_title")}
              </Tooltip>
            </TH>

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.all.last_submitted_header_tooltip")}>
                {t("assessment_dashboard.all.last_submitted_header_title")}
              </Tooltip>
            </TH>
          </TR>
        </THead>

        <tbody>
          {allAssessmentsTableRows.map((row) => (
            <TR key={row.id}>
              <TD className="max-w-52 truncate py-2.5 pl-[17px]" title={row.topicTitle || "–"}>
                {row.topicTitle || "–"}
              </TD>

              <TD className="max-w-40 truncate" title={row.page.title}>
                <PageLink
                  className="w-full truncate text-blue-shade-20 underline"
                  page={row.page}
                />
              </TD>

              <TD className="max-w-40 truncate">
                <StatusCell row={row} sectionId={sectionId} />
              </TD>

              <TD>
                <PublishStatus
                  status={
                    row.publishing === "Published"
                      ? "published"
                      : row.publishing === "Draft"
                        ? "draft"
                        : "na"
                  }
                />
              </TD>

              <TD>{row.publishing === "-" ? "-" : <AssessedCount page={row.page} />}</TD>

              <TD>{row.pageType}</TD>

              <TD>{row.avgPoints}</TD>

              <TD>
                <Tooltip text={row.outcomesTooltip}>{row.outcomesCount.toString()}</Tooltip>
              </TD>

              <TD>{row.lastSubmittedDate}</TD>

              {!hideMarkCompleteMenu && (
                <td>
                  <MarkAssessmentCompleteMenu
                    isMarkedComplete={Boolean(row.page.assessing_marked_complete_at)}
                    pageId={row.page.id}
                    pageTitle={row.page.title}
                    sectionId={sectionId}
                    isCourseAssessment
                    onChange={refetch}
                  />
                </td>
              )}
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
